import { useCallback } from 'react';
import { Editor, Path, Range, Transforms } from 'slate';
import { ReactEditor } from 'slate-react';
import { splitTextNodeOnSpace } from '../utils/splitTextNodeOnSpace';

export const useEditorKeyDownHandler = (editor) => {
  return useCallback(
    (event) => {
      const cursorPoint = editor.selection ? editor.selection.anchor : null;
      if (cursorPoint) {
        const { path, offset } = cursorPoint;
        const [currentNode] = Editor.node(editor, path);
        const parentPath = Path.parent(path);
        const [parentNode] = Editor.node(editor, parentPath);

        const isLastNode = path[1] === (parentNode.children.length || 0) - 1;
        const lastIndex = currentNode?.text?.length || 0;
        if (event.key === 'Backspace') {
          const { selection } = editor;
          if (!selection) return;
          if (Range.isCollapsed(selection)) {
            const { anchor } = selection;
            const currentPath = Path.parent(anchor.path);

            if (anchor.offset === 0) {
              try {
                const prevPath = Path.previous(currentPath);

                if (Editor.hasPath(editor, prevPath)) {
                  const [prevNode] = Editor.node(editor, prevPath);

                  const prevNodeText = prevNode.children.map((child) => child.text).join('');

                  if ((prevNode.type === 'paragraph' || prevNode.type === 'speaker') && prevNodeText === '') {
                    event.preventDefault();

                    const [currentNode] = Editor.node(editor, currentPath);

                    Transforms.removeNodes(editor, { at: currentPath });

                    Transforms.insertNodes(editor, currentNode.children, {
                      at: [...prevPath, 0],
                    });

                    Transforms.select(editor, {
                      anchor: { path: [...prevPath, 0], offset: 0 },
                      focus: { path: [...prevPath, 0], offset: 0 },
                    });
                  }
                }
              } catch (err) {
                console.log('Path.previous may throw on first block', err);
              }
            }
          }
        } else if (event.key === 'Enter' && !event.shiftKey && !event.ctrlKey) {
          if (path[1] === 0 && offset === 0) {
            event.preventDefault();
            Transforms.setNodes(editor, { type: 'speaker' });
          } else if (isLastNode && offset >= lastIndex) {
            event.preventDefault();
          } else {
            event.preventDefault();
            const { selection } = editor;
            if (!selection) return;
            Transforms.splitNodes(editor);
            if (editor.selection.anchor.offset === 0) {
              Transforms.setNodes(
                editor,
                { type: 'paragraph', speaker: '', id: crypto.randomUUID().toString(), examination: null },
                { at: editor.selection },
              );
            } else {
              Transforms.select(editor, {
                anchor: { path: [path[0] + 1, 0], offset: 0 },
                focus: { path: [path[0] + 1, 0], offset: 0 },
              });
              Transforms.setNodes(
                editor,
                { type: 'paragraph', speaker: '', id: crypto.randomUUID().toString(), examination: null },
                { at: editor.selection },
              );
              setTimeout(() => {
                const domNode = ReactEditor.toDOMNode(editor, Editor.node(editor, [path[0] + 1])[0]);
                domNode?.scrollIntoView({ behavior: 'smooth', block: 'start' });
                ReactEditor.focus(editor);
              }, 0);
            }
          }
        } else if (event.key === 'Enter' && event.shiftKey && !event.ctrlKey) {
          event.preventDefault();
          const { selection } = editor;
          const { focus } = selection;
          Transforms.insertText(editor, '\n', { at: focus });
        } else if (event.key === 'Enter' && event.shiftKey && event.ctrlKey) {
          event.preventDefault();
          const parentPath = Path.parent(path);
          const [parentNode] = Editor.node(editor, parentPath);
          const slateSpeakerLabelComponent = document.getElementById(`menu-${parentNode.id}`);
          if (slateSpeakerLabelComponent) {
            slateSpeakerLabelComponent.click();
          }
        } else if (event.key === 'Enter' && event.ctrlKey && !event.shiftKey) {
          event.preventDefault();
          const parentPath = Path.parent(path);
          const nextPath = Path.next(parentPath);
          const [nextNode] = Editor.node(editor, nextPath);
          const slateSpeakerLabelComponent = document.getElementById(`menu-${nextNode.id}`);
          if (slateSpeakerLabelComponent) {
            slateSpeakerLabelComponent.scrollIntoView({
              behavior: 'smooth',
              block: 'center',
            });
            setTimeout(() => {
              slateSpeakerLabelComponent.click();
            }, 200);
            Transforms.select(editor, {
              anchor: { path: [...nextPath, 0], offset: 0 },
              focus: { path: [...nextPath, 0], offset: 0 },
            });

            setTimeout(() => {
              ReactEditor.focus(editor);
            }, 100);
          }
        } else if (event.ctrlKey && (event.key === 'm' || event.key === 'M') && !event.shiftKey) {
          event.preventDefault();
          const { selection } = editor;
          Transforms.setNodes(editor, { completed: !parentNode.completed }, { at: selection });
        } else if (event.key === ' ') {
          event.preventDefault();
          splitTextNodeOnSpace(editor);
        }
      }
    },
    [editor],
  );
};
